import {
  type FormHelperTextProps,
  type SxProps,
  type Theme,
  useFormControl,
  Tooltip,
} from '@mui/material';
import { forwardRef } from 'react';
import { Typography } from '../../primitives/typography/typography';

type OwnerState = {
  error?: boolean;
  disabled?: boolean;
  color?: string;
};

/**
 * Teach `TextField`'s `slotProps.formHelperText` about {@link FieldHelpTextProps.wrap}.
 *
 * `FieldHelpText` is wired in as the `formHelperText` slot for every `MuiTextField` and
 * `MuiPickersTextField` (see `theme/theme.ts`), but MUI types that slot's props from
 * `FormHelperTextProps` — so without this augmentation a caller passing `wrap` gets a TS2353
 * "does not exist" even though it reaches the component perfectly well at runtime.
 */
declare module '@mui/material/TextField' {
  interface TextFieldFormHelperTextSlotPropsOverrides {
    wrap?: boolean;
  }
}

type FieldHelpTextProps = Omit<FormHelperTextProps, 'variant'> & {
  ownerState?: OwnerState;
  /**
   * Opt in to **multi-line help** — the text wraps and the field grows to fit it, instead of being
   * ellipsised onto one line with the rest behind a hover tooltip.
   *
   * Reach for it when the help is a sentence the user actually has to read, and especially on a
   * **touch surface**: {@link baseStyles} leans on a `Tooltip` to reveal the truncated remainder,
   * and there is no hover on a phone, so on Trackd the tail of a long sentence is simply
   * unreadable. (Found on the police block's "an event number may not have been issued yet" help,
   * which is the one sentence stopping a guard from filing a report they think is incomplete.)
   *
   * Off by default: the default is what keeps every field a fixed height regardless of its help,
   * and changing that would move layout in every app that already relies on it.
   */
  wrap?: boolean;
};

/**
 * The default: one line, ellipsised, the remainder on hover. It is `position: absolute` so help
 * never changes a field's height — which is *why* it cannot wrap.
 */
const baseStyles: SxProps<Theme> = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  position: 'absolute',
  top: '100%',
  left: 14,
  right: 0,
};

/**
 * The {@link FieldHelpTextProps.wrap} variant. Going back into normal flow is the whole point — an
 * absolutely-positioned second line would sit on top of whatever follows the field. `14px` keeps it
 * aligned with the input's text, matching {@link baseStyles}' `left`.
 */
const wrapStyles: SxProps<Theme> = {
  whiteSpace: 'normal',
  overflow: 'visible',
  textOverflow: 'clip',
  position: 'static',
  display: 'block',
  ml: '14px',
  mt: 0.5,
};

export const FieldHelpText = forwardRef<HTMLSpanElement, FieldHelpTextProps>(
  ({ children, ownerState, sx, wrap = false, ...props }, ref) => {
    const formControl = useFormControl();

    const error = ownerState?.error ?? formControl?.error ?? false;
    const disabled = ownerState?.disabled ?? formControl?.disabled ?? false;
    const color = ownerState?.color ?? formControl?.color ?? 'primary';

    const colorSx: SxProps<Theme> = getStateStyles(error, disabled, color);
    const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

    const inFlow = wrap || error;

    const caption = (
      <Typography.Caption
        ref={ref}
        {...props}
        sx={[inFlow ? wrapStyles : baseStyles, colorSx, ...sxOverrides]}
      >
        {children}
      </Typography.Caption>
    );

    // Nothing is hidden when it wraps, so there is nothing for a tooltip to reveal, and the
    // tooltip is unreachable on the touch surfaces this variant exists for anyway.
    if (inFlow) return caption;

    const toolTip = children !== '\u00A0' ? children : undefined;

    return (
      <Tooltip title={toolTip ?? ''} enterDelay={200} disableInteractive>
        {caption}
      </Tooltip>
    );
  },
);

const getStateStyles = (error: boolean, disabled: boolean, color: string): SxProps<Theme> => {
  if (disabled) return { color: 'text.disabled' };
  // Per-brand, because the AA-passing red depends on the canvas the brand sits on — see
  // `staticVars.fieldErrorText`. Falls back to `error.main` for a theme that predates the knob.
  if (error) return (theme) => ({ color: theme.staticVars?.fieldErrorText ?? 'error.main' });
  // Kept in step with `FIELD_STATE_COLOURS` in `theme/theme.ts`, which tints the outline and label
  // for the same states — help text in a different colour to its own field reads as a third state.
  if (color === 'warning' || color === 'success') return { color: `${color}.main` };
  return { color: 'text.secondary' };
};

FieldHelpText.displayName = 'FormHelperTextCaption';
